import React, { useEffect, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import Card from "react-bootstrap/Card";
import { useAccordionButton } from "react-bootstrap";
import { IoIosArrowDown } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import {
    addFilterToCategoryAction,
    addFilterGroupToCategoryAction,
    assignEasyAccessFilterAction,
    updateCategoryAction,
} from "../../../utils/redux/category/categoryActions";
import ProductionComponentsManager from "./ProductionComponentsManager";

// Chip component for filters
const FilterChip = ({ name, onRemove }) => (
    <span
        style={{
            backgroundColor: "#E9ECEF",
            color: "black",
            padding: "4px 8px",
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "0.875rem",
        }}
    >
        {name}
        {onRemove && (
            <button
                style={{
                    border: "none",
                    background: "none",
                    padding: 0,
                    cursor: "pointer",
                    fontWeight: "bold",
                    lineHeight: 1,
                    fontSize: "1.2rem",
                }}
                onClick={onRemove}
                title="Remove"
            >
                ×
            </button>
        )}
    </span>
);

function CustomToggle({ eventKey }) {
    const onClick = useAccordionButton(eventKey);
    return (
        <button type="button" onClick={onClick} style={{ background: "none", border: "none" }}>
            <IoIosArrowDown />
        </button>
    );
}

const CategoryCard = ({ category, index }) => {
    const dispatch = useDispatch();
    const allFilterGroups = useSelector((state) => state.filter.filters);
    const [activeTab, setActiveTab] = useState('filters'); // 'filters' or 'components'
    const [selectedFilterGroup, setSelectedFilterGroup] = useState("");
    const [selectedFilter, setSelectedFilter] = useState({});
    const [selectedEasyAccess, setSelectedEasyAccess] = useState("");
    const [savingComponents, setSavingComponents] = useState(false);

    const { easyAccessFilters = [], filterGroups = [], productionComponents = [] } = category;

    // Dropdown: Filter Groups not yet assigned to this category
    const filterGroupOptions = allFilterGroups?.filter(
        (fg) => !filterGroups.some((g) => g._id === fg._id)
    );

    // Dropdown: Filters assigned to category but not yet marked Easy Access
    const easyAccessOptions = filterGroups
        .flatMap((g) => g.filters)
        .filter((f) => !easyAccessFilters.some((ef) => ef._id === f._id));

    // Add Filter Group
    const handleAddFilterGroup = async () => {
        if (!selectedFilterGroup) return;
        const fg = allFilterGroups.find((g) => g._id === selectedFilterGroup);
        if (!fg) return;

        const res = await dispatch(addFilterGroupToCategoryAction(category._id, fg._id, fg.name));
        if (res.success) setSelectedFilterGroup("");
    };

    // Add Filter
    const handleAddFilter = async (groupId) => {
        const filterId = selectedFilter[groupId];
        if (!filterId) return;

        const filterObj = allFilterGroups
            .find((fg) => fg._id === groupId)
            ?.filters.find((f) => f._id === filterId);

        if (!filterObj) return;

        const res = await dispatch(
            addFilterToCategoryAction(category._id, groupId, filterObj._id, filterObj.name)
        );

        if (res.success) {
            setSelectedFilter((prev) => ({ ...prev, [groupId]: "" }));
        }
    };

    // Add/Remove Easy Access Filter
    const handleEasyAccess = async (filterId) => {
        if (!filterId) return;
        const res = await dispatch(assignEasyAccessFilterAction(category._id, filterId));
        if (res.success) setSelectedEasyAccess("");
    };

    // Handle production components update
    const handleProductionComponentsUpdate = async (components) => {
        setSavingComponents(true);
        try {
            const result = await dispatch(updateCategoryAction(category._id, {
                productionComponents: components
            }));
            if (result.success) {
                alert('Production components saved successfully!');
            } else {
                alert(result.message || 'Failed to save production components');
            }
        } catch (error) {
            console.error('Error saving production components:', error);
            alert('Error saving production components');
        } finally {
            setSavingComponents(false);
        }
    };

    return (
        <div className="w-100 p-3 my-2 bg-light">
            {/* HEADER */}
            <div className="d-flex align-items-center">
                <CustomToggle eventKey={index} />
                <strong className="ms-2">
                    {index + 1}. {category.name}
                </strong>
            </div>

            <Accordion.Collapse eventKey={index}>
                <Card.Body>
                    {/* Tab Navigation */}
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'filters' ? 'active' : ''}`}
                                onClick={() => setActiveTab('filters')}
                            >
                                Filters & Easy Access
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'components' ? 'active' : ''}`}
                                onClick={() => setActiveTab('components')}
                            >
                                Production Components
                            </button>
                        </li>
                    </ul>

                    {/* Filters Tab */}
                    {activeTab === 'filters' && (
                        <>
                            {/* ================= EASY ACCESS SECTION ================= */}
                            <section className="mb-4 mt-2">
                                <div className="d-flex align-items-start mb-2">
                                    <div style={{ minWidth: "180px", fontWeight: "500" }}>
                                        Easy Access Filters:
                                    </div>

                                    <div className="flex-grow-1">
                                        <div className="d-flex mb-2">
                                            <select
                                                className="form-select form-select-sm w-50 mb-2"
                                                value={selectedEasyAccess}
                                                onChange={(e) => setSelectedEasyAccess(e.target.value)}
                                            >
                                                <option value="">+ Add Easy Access Filter</option>
                                                {easyAccessOptions?.map((f) => (
                                                    <option key={f._id} value={f._id}>
                                                        {f.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                className="btn btn-sm btn-outline-primary mb-2 ms-2"
                                                onClick={() => handleEasyAccess(selectedEasyAccess)}
                                            >
                                                Add
                                            </button>
                                        </div>

                                        <div className="d-flex flex-wrap gap-2">
                                            {easyAccessFilters?.length ? (
                                                easyAccessFilters?.map((f) => (
                                                    <FilterChip
                                                        key={f._id}
                                                        name={f.name}
                                                        onRemove={() => handleEasyAccess(f._id)}
                                                    />
                                                ))
                                            ) : (
                                                <span className="text-muted">
                                                    No easy access filters assigned
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <hr />

                            {/* ================= FILTER GROUPS SECTION ================= */}
                            <section>
                                {/* ADD NEW FILTER GROUP */}
                                <div className="d-flex flex-row justify-content-start align-items-center mb-3">
                                    <div className="me-3">Filter Types: </div>
                                    <select
                                        className="form-select form-select-sm w-auto me-2"
                                        value={selectedFilterGroup}
                                        onChange={(e) => setSelectedFilterGroup(e.target.value)}
                                    >
                                        <option value="">+ Add new filter type</option>
                                        {filterGroupOptions?.map((fg) => (
                                            <option key={fg._id} value={fg._id}>
                                                {fg.name}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={handleAddFilterGroup}
                                    >
                                        Add
                                    </button>
                                </div>

                                {/* Existing filter groups */}
                                {filterGroups?.length ? (
                                    filterGroups?.map((group) => {
                                        const groupFilters = allFilterGroups
                                            .find((fg) => fg._id === group._id)
                                            ?.filters.filter(
                                                (f) => !group.filters.some((gf) => gf._id === f._id)
                                            );

                                        return (
                                            <div
                                                key={group._id}
                                                className="border rounded p-3 mb-3 bg-white"
                                            >
                                                <div className="d-flex align-items-start">
                                                    <div style={{ minWidth: "180px", fontWeight: "500" }}>
                                                        {group.name}:
                                                    </div>

                                                    <div className="flex-grow-1">
                                                        <div className="d-flex mb-2">
                                                            <select
                                                                className="form-select form-select-sm w-50 mb-2"
                                                                value={selectedFilter[group._id] || ""}
                                                                onChange={(e) =>
                                                                    setSelectedFilter((prev) => ({
                                                                        ...prev,
                                                                        [group._id]: e.target.value,
                                                                    }))
                                                                }
                                                            >
                                                                <option value="">+ Add Filter to Category</option>
                                                                {groupFilters?.map((f) => (
                                                                    <option key={f._id} value={f._id}>
                                                                        {f.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary mb-2 ms-2"
                                                                onClick={() => handleAddFilter(group._id)}
                                                            >
                                                                Add
                                                            </button>
                                                        </div>

                                                        <div className="d-flex flex-wrap gap-2">
                                                            {group?.filters?.length ? (
                                                                group?.filters?.map((f) => (
                                                                    <FilterChip key={f._id} name={f.name} />
                                                                ))
                                                            ) : (
                                                                <span className="text-muted">No filters assigned</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <span className="text-muted">
                                        No filter groups assigned to this category
                                    </span>
                                )}
                            </section>
                        </>
                    )}

                    {/* Production Components Tab */}
                    {activeTab === 'components' && (
                        <ProductionComponentsManager
                            categoryId={category._id}
                            categoryName={category.name}
                            initialComponents={productionComponents || []}
                            onSave={handleProductionComponentsUpdate}
                            saving={savingComponents}
                        />
                    )}
                </Card.Body>
            </Accordion.Collapse>
        </div>
    );
};

export default CategoryCard;